(() => {
  const storageKey = 'hobbytiger:favorites';
  const pageStatePrefix = 'hobbytiger:favorites-page-state:';
  const pageStateMaxAge = 30 * 60 * 1000;
  const maxItems = 200;

  const pageStatePath = () => `${window.location.pathname}${window.location.search}`;
  const pageStateKey = () => `${pageStatePrefix}${pageStatePath()}`;

  const favoritesPage = () => document.querySelector('[data-hobbytiger-favorites-page]');

  const getLocalePrefix = () => {
    const match = window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/i);
    if (!match) return '';

    const prefix = `/${match[1].toLowerCase()}`;
    return prefix === '/en' ? '' : prefix;
  };

  const localizeDrawerLinks = () => {
    const localePrefix = getLocalePrefix();
    if (!localePrefix) return;

    document.querySelectorAll('#menu-drawer a[href^="/"]:not([href^="//"])').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || href === localePrefix || href.startsWith(`${localePrefix}/`)) return;
      link.setAttribute('href', `${localePrefix}${href}`);
    });
  };

  const getThemeStrings = () => window.HobbyTigerThemeStrings || {};
  const productCardStrings = () => getThemeStrings().productCard || {};
  const favoritesStrings = () => getThemeStrings().favorites || {};
  const phrase = (value, replacements = {}) => Object.entries(replacements).reduce(
    (text, [key, replacement]) => text.replaceAll(`[${key}]`, replacement),
    value || ''
  );
  const localizedPath = (path) => {
    const localePrefix = getLocalePrefix();
    if (!localePrefix || !path.startsWith('/')) return path;
    if (path === localePrefix || path.startsWith(`${localePrefix}/`)) return path;
    return `${localePrefix}${path}`;
  };

  const revealStickyHeader = () => {
    const headerSection = document.querySelector('.section-header');
    if (!headerSection) return;
    headerSection.classList.add('shopify-section-header-sticky', 'animate');
    headerSection.classList.remove('shopify-section-header-hidden');
    document.documentElement.style.setProperty('--header-height', `${headerSection.offsetHeight}px`);
  };

  const isBackForwardNavigation = (event = null) => {
    if (event?.persisted) return true;
    try {
      return performance.getEntriesByType?.('navigation')?.[0]?.type === 'back_forward';
    } catch (error) {
      return false;
    }
  };

  const savePageState = () => {
    if (!favoritesPage()) return;
    try {
      sessionStorage.setItem(pageStateKey(), JSON.stringify({
        path: pageStatePath(),
        scrollY: Math.round(window.scrollY || document.documentElement.scrollTop || 0),
        favoriteHandles: readFavorites().map((item) => item.handle).join('|'),
        savedAt: Date.now(),
      }));
    } catch (error) {
      /* sessionStorage can be unavailable in private modes. */
    }
  };

  const readPageState = () => {
    try {
      const state = JSON.parse(sessionStorage.getItem(pageStateKey()) || 'null');
      if (!state || state.path !== pageStatePath()) return null;
      if (Date.now() - Number(state.savedAt || 0) > pageStateMaxAge) return null;
      return state;
    } catch (error) {
      return null;
    }
  };

  const restorePageScroll = () => {
    if (!favoritesPage()) return;
    const state = readPageState();
    const targetScrollY = Number(state?.scrollY || 0);
    if (targetScrollY <= 0) return;
    revealStickyHeader();
    window.requestAnimationFrame(() => {
      revealStickyHeader();
      window.scrollTo(0, targetScrollY);
    });
    [80, 240, 500].forEach((delay) => {
      window.setTimeout(() => {
        revealStickyHeader();
        window.scrollTo(0, targetScrollY);
      }, delay);
    });
  };

  const readFavorites = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => item && item.handle) : [];
    } catch (error) {
      return [];
    }
  };

  const writeFavorites = (items) => {
    localStorage.setItem(storageKey, JSON.stringify(items.slice(0, maxItems)));
    window.dispatchEvent(new CustomEvent('hobbytiger:favorites:change'));
  };

  const escapeHtml = (value) =>
    String(value || '').replace(/[&<>"']/g, (character) => {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      };
      return map[character];
    });

  const byHandle = (handle) => (item) => item.handle === handle;

  const getButtonItem = (button) => ({
    handle: button.dataset.productHandle,
    title: button.dataset.productTitle || productCardStrings().thisProduct || 'Product',
    url: localizedPath(button.dataset.productUrl || `/products/${button.dataset.productHandle}`),
    image: button.dataset.productImage || '',
    price: button.dataset.productPrice || '',
    productType: button.dataset.productType || '',
    preorderPill: button.dataset.productPreorderPill || '',
    addedAt: new Date().toISOString(),
  });

  const updateFavoriteButtons = () => {
    const favorites = readFavorites();

    document.querySelectorAll('[data-hobbytiger-favorite-button]').forEach((button) => {
      const handle = button.dataset.productHandle;
      const active = Boolean(handle && favorites.some(byHandle(handle)));
      const title = button.dataset.productTitle || productCardStrings().thisProduct || 'this product';
      const ariaTemplate = active
        ? favoritesStrings().removeAria
        : productCardStrings().favoriteAria;

      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute(
        'aria-label',
        phrase(ariaTemplate, { title }) || `${active ? 'Remove' : 'Add'} ${title} ${active ? 'from' : 'to'} favorites`
      );
    });
  };

  const updateFavoriteCounts = () => {
    const count = readFavorites().length;

    document.querySelectorAll('[data-hobbytiger-favorite-count]').forEach((element) => {
      element.textContent = String(count);
      element.hidden = count === 0;
    });
  };

  const hydrateFavorite = async (item) => {
    try {
      const response = await fetch(localizedPath(`/products/${encodeURIComponent(item.handle)}?view=favorite-data`));
      if (!response.ok) return item;

      const freshItem = await response.json();
      return {
        ...item,
        ...freshItem,
        addedAt: item.addedAt,
      };
    } catch (error) {
      return item;
    }
  };

  const fetchFavoriteCard = async (item) => {
    try {
      const response = await fetch(localizedPath(`/products/${encodeURIComponent(item.handle)}?view=favorite-card`));
      if (!response.ok) return '';

      return await response.text();
    } catch (error) {
      return '';
    }
  };

  const getFavoriteType = (item) => {
    const type = String(item?.productType || item?.type || '').trim();
    return type || 'Products';
  };

  const renderFavoritesPage = async () => {
    const page = favoritesPage();
    if (!page) return;

    const grid = page.querySelector('[data-hobbytiger-favorites-grid]');
    const empty = page.querySelector('[data-hobbytiger-favorites-empty]');
    const clearButton = page.querySelector('[data-hobbytiger-clear-favorites]');
    const favorites = readFavorites();
    const favoriteHandles = favorites.map((item) => item.handle).join('|');

    if (!grid || !empty) return;

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    if (grid.dataset.htRenderedFavoriteHandles === favoriteHandles && grid.children.length > 0) {
      empty.hidden = true;
      grid.hidden = false;
      if (clearButton) clearButton.hidden = favorites.length === 0;
      page.classList.add('is-ready');
      updateFavoriteButtons();
      return;
    }

    page.classList.remove('is-ready');

    if (clearButton) clearButton.hidden = favorites.length === 0;

    if (favorites.length === 0) {
      empty.hidden = false;
      grid.hidden = true;
      grid.innerHTML = '';
      grid.dataset.htRenderedFavoriteHandles = '';
      page.classList.add('is-ready');
      updateFavoriteButtons();
      return;
    }

    empty.hidden = true;
    grid.hidden = false;

    const hydratedFavorites = await Promise.all(favorites.map(hydrateFavorite));
    const cardHtml = await Promise.all(hydratedFavorites.map(fetchFavoriteCard));
    const groups = [];
    const groupByType = new Map();

    hydratedFavorites.forEach((item, index) => {
      const type = getFavoriteType(item);

      if (!groupByType.has(type)) {
        const group = { type, entries: [] };
        groupByType.set(type, group);
        groups.push(group);
      }

      groupByType.get(type).entries.push({
        html: cardHtml[index] || '',
        item,
      });
    });

    let revealIndex = 0;
    grid.innerHTML = groups
      .map((group) => {
        const cards = group.entries
          .map(({ html, item }) => {
            revealIndex += 1;
            const revealAttributes = `class="ht-favorites-card scroll-trigger animate--slide-in" style="--animation-order: ${revealIndex};"`;

            if (html.trim()) {
              return `<div ${revealAttributes} data-hobbytiger-favorites-card="${escapeHtml(item.handle)}">${html}</div>`;
            }

            const url = localizedPath(item.url || `/products/${item.handle}`);
            return `
              <div ${revealAttributes} data-hobbytiger-favorites-card="${escapeHtml(item.handle)}">
                <a href="${escapeHtml(url)}">${escapeHtml(item.title || item.handle)}</a>
              </div>
            `;
          })
          .join('');

        return `
          <section class="ht-favorites-group" data-hobbytiger-favorites-group="${escapeHtml(group.type)}">
            <h2 class="ht-favorites-group__heading">${escapeHtml(group.type)}</h2>
            <div class="ht-favorites-grid">${cards}</div>
          </section>
        `;
      })
      .join('');
    grid.dataset.htRenderedFavoriteHandles = favoriteHandles;

    page.classList.add('is-ready');

    updateFavoriteButtons();
    restorePageScroll();
  };

  const refresh = () => {
    localizeDrawerLinks();
    updateFavoriteButtons();
    updateFavoriteCounts();
    renderFavoritesPage();
  };

  const findFavoriteButtonFromClick = (event) => {
    const directButton = event.target.closest('[data-hobbytiger-favorite-button]');
    if (directButton) return directButton;

    return (
      document
        .elementsFromPoint(event.clientX, event.clientY)
        .map((element) => element.closest && element.closest('[data-hobbytiger-favorite-button]'))
        .find(Boolean) || null
    );
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-hobbytiger-favorites-page] a[href*="/products/"]')) {
      savePageState();
    }

    const favoriteButton = findFavoriteButtonFromClick(event);
    const removeButton = event.target.closest('[data-hobbytiger-remove-favorite]');
    const clearButton = event.target.closest('[data-hobbytiger-clear-favorites]');

    if (clearButton) {
      event.preventDefault();
      writeFavorites([]);
      return;
    }

    if (favoriteButton) {
      event.preventDefault();
      event.stopPropagation();

      const item = getButtonItem(favoriteButton);
      if (!item.handle) return;

      const favorites = readFavorites();
      const exists = favorites.some(byHandle(item.handle));
      const nextFavorites = exists ? favorites.filter((favorite) => favorite.handle !== item.handle) : [item, ...favorites];

      writeFavorites(nextFavorites);
      return;
    }

    if (removeButton) {
      const handle = removeButton.dataset.productHandle;
      if (!handle) return;

      writeFavorites(readFavorites().filter((favorite) => favorite.handle !== handle));
    }
  }, true);

  document.addEventListener('DOMContentLoaded', refresh);
  document.addEventListener('shopify:section:load', refresh);
  window.addEventListener('pagehide', savePageState);
  window.addEventListener('beforeunload', savePageState);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') savePageState();
  });
  window.addEventListener('pageshow', (event) => {
    if (isBackForwardNavigation(event)) {
      refresh();
      restorePageScroll();
    }
  });
  window.addEventListener('hobbytiger:favorites:change', refresh);
})();
